import { useUIv2 } from '@/hooks/useUIv2'
import ConnectAccountsV2 from './ConnectAccountsV2'
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowRight, ArrowLeft, ChevronRight, CheckCircle } from 'lucide-react'
import PageLayout from '@/components/layout/PageLayout'
import { storeDigitalCoworkerOAuthState } from '@/components/DigitalCoworkerGoogleCallback'
import { storeDigitalCoworkerOneDriveOAuthState } from '@/components/DigitalCoworkerOneDriveCallback'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'

const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/calendar',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/drive.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
].join(' ')

const MS_SCOPES = 'User.Read Files.Read.All offline_access'

const ConnectAccounts = () => {
  const isV2 = useUIv2()
  if (isV2) return <ConnectAccountsV2 />
  return <ConnectAccountsLegacy />
}

const ConnectAccountsLegacy = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const coworkerId = location.state?.coworkerId as string | undefined
  const formData = location.state?.formData ?? {}

  const [isGoogleConnected, setIsGoogleConnected] = useState(
    location.state?.googleConnected === true,
  )
  const [googleEmail, setGoogleEmail] = useState<string | null>(
    location.state?.googleEmail ?? null,
  )
  const [isOneDriveConnected, setIsOneDriveConnected] = useState(
    location.state?.oneDriveConnected === true,
  )
  const [oneDriveEmail, setOneDriveEmail] = useState<string | null>(
    location.state?.oneDriveEmail ?? null,
  )

  // Update state when returning from OAuth callback
  useEffect(() => {
    if (location.state?.googleConnected) {
      setIsGoogleConnected(true)
      setGoogleEmail(location.state.googleEmail ?? null)
    }
    if (location.state?.oneDriveConnected) {
      setIsOneDriveConnected(true)
      setOneDriveEmail(location.state.oneDriveEmail ?? null)
    }
  }, [location.state])

  const handleConnectGoogle = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_client_id'))
      return
    }
    if (!coworkerId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_coworker_id'))
      navigate('/digital-coworker/new', { state: { formData } })
      return
    }

    const state = crypto.randomUUID()
    const formDataWithConnections = {
      ...formData,
      oneDriveConnected: isOneDriveConnected,
      oneDriveEmail: oneDriveEmail ?? undefined,
    }
    storeDigitalCoworkerOAuthState(coworkerId, state, formDataWithConnections)

    const redirectUri = `${window.location.origin}/auth/callback/digital-coworker-google`
    const oauthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(GOOGLE_SCOPES)}` +
      `&state=${encodeURIComponent(state)}` +
      `&access_type=offline` +
      `&prompt=consent`

    window.location.href = oauthUrl
  }

  const handleConnectOneDrive = () => {
    const clientId = import.meta.env.VITE_MS_TEAMS_CLIENT_ID
    if (!clientId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_onedrive_client_id'))
      return
    }
    if (!coworkerId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_coworker_id'))
      navigate('/digital-coworker/new', { state: { formData } })
      return
    }

    const state = crypto.randomUUID()
    const formDataWithConnections = {
      ...formData,
      googleConnected: isGoogleConnected,
      googleEmail: googleEmail ?? undefined,
    }
    storeDigitalCoworkerOneDriveOAuthState(coworkerId, state, formDataWithConnections)

    const redirectUri = `${window.location.origin}/auth/callback/digital-coworker-onedrive`
    const oauthUrl =
      `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize` +
      `?response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(MS_SCOPES)}` +
      `&state=${encodeURIComponent(state)}` +
      `&prompt=consent`

    window.location.href = oauthUrl
  }

  const handleContinue = () => {
    navigate('/digital-coworker/setup-interviews', {
      state: {
        formData: {
          ...formData,
          googleConnected: isGoogleConnected,
          googleEmail: googleEmail ?? undefined,
          oneDriveConnected: isOneDriveConnected,
          oneDriveEmail: oneDriveEmail ?? undefined,
        },
        coworkerId,
      },
    })
  }

  const handleBack = () => {
    navigate('/digital-coworker/new', { state: { formData, coworkerId } })
  }

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto py-8">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          <button
            onClick={() => navigate('/digital-coworker')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-dark-text transition-colors"
          >
            {t('digital_coworker:breadcrumb.list')}
          </button>
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>

        <div className="space-y-8">
          {/* Header Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">{t('digital_coworker:connect_accounts.title')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('digital_coworker:connect_accounts.subtitle')}
            </p>
          </div>

          {/* Connection Options */}
          <div className="space-y-4">
            {/* Google Account Connection */}
            <div className="flex items-center justify-between p-4 border dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{t('digital_coworker:connect_accounts.google.title')}</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {isGoogleConnected && googleEmail
                      ? t('digital_coworker:connect_accounts.google.connected_as', { email: googleEmail })
                      : t('digital_coworker:connect_accounts.google.description')}
                  </p>
                </div>
              </div>
              {isGoogleConnected ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">{t('digital_coworker:connect_accounts.google.connected_status')}</span>
                </div>
              ) : (
                <Button variant="default" size="sm" onClick={handleConnectGoogle}>
                  {t('digital_coworker:connect_accounts.google.connect_button')}
                </Button>
              )}
            </div>

            {/* OneDrive Connection */}
            <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <svg viewBox="0 0 22 22" className="w-7 h-7">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="12" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="12" width="9" height="9" fill="#00A4EF" />
                    <rect x="12" y="12" width="9" height="9" fill="#FFB900" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{t('digital_coworker:connect_accounts.onedrive.title')}</h3>
                  <p className="text-xs text-gray-600">
                    {isOneDriveConnected && oneDriveEmail
                      ? t('digital_coworker:connect_accounts.onedrive.connected_as', { email: oneDriveEmail })
                      : t('digital_coworker:connect_accounts.onedrive.description')}
                  </p>
                </div>
              </div>
              {isOneDriveConnected ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">{t('digital_coworker:connect_accounts.onedrive.connected_status')}</span>
                </div>
              ) : (
                <Button variant="default" size="sm" onClick={handleConnectOneDrive}>
                  {t('digital_coworker:connect_accounts.onedrive.connect_button')}
                </Button>
              )}
            </div>

            {/* Upload Data - Disabled */}
            <div className="flex items-center justify-between p-4 border dark:border-dark-border rounded-lg bg-gray-50 dark:bg-dark-muted opacity-60">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-600 dark:text-gray-400">{t('digital_coworker:connect_accounts.upload_data.title')}</h3>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                    {t('digital_coworker:connect_accounts.upload_data.description')}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" disabled>
                {t('digital_coworker:connect_accounts.upload_data.button')}
              </Button>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between items-center pt-6 border-t">
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('digital_coworker:buttons.back')}
            </Button>
            <Button onClick={handleContinue} className="gap-2">
              {t('digital_coworker:buttons.continue')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default ConnectAccounts
